import React, { useState, useEffect } from 'react';
import { specialties as defaultSpecialties, renovationInfo as defaultRenovation } from '../data/mock';
import { Users, Home, ChevronLeft, ChevronRight, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const figureIcons = [Users, Home];

// Default images for specialties
const defaultImages = {
  "carrelage": ["https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800&q=80"],
  "sols-souples": ["https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=800&q=80"],
  "parquet": ["https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80"],
  "peinture": ["https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=800&q=80"],
  "renovation": ["https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800&q=80"],
  "agrandissement": ["https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80"]
};

// Image Carousel Component
const ImageCarousel = ({ images, title, onImageClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) return null;

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="relative w-full h-full group">
      <img
        src={images[currentIndex]}
        alt={`${title} ${currentIndex + 1}`}
        className="w-full h-full object-cover cursor-pointer transition-transform duration-700 group-hover:scale-105"
        onClick={() => onImageClick(images, currentIndex)}
      />
      
      {images.length > 1 && (
        <>
          {/* Navigation arrows */}
          <button
            onClick={prevImage}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronRight size={20} />
          </button>
          
          {/* Dots indicator */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentIndex ? 'bg-white w-4' : 'bg-white/50 hover:bg-white/70'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Lightbox Component for zoomed view
const Lightbox = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setCurrentIndex((prev) => (prev + 1) % images.length);
      if (e.key === 'ArrowLeft') setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [images.length, onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={onClose}>
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
      >
        <X size={28} />
      </button>
      
      <button
        onClick={(e) => { e.stopPropagation(); setCurrentIndex((prev) => (prev - 1 + images.length) % images.length); }}
        className="absolute left-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
      >
        <ChevronLeft size={28} />
      </button>
      
      <img
        src={images[currentIndex]}
        alt={`Image ${currentIndex + 1}`}
        className="max-w-[90vw] max-h-[85vh] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      
      <button
        onClick={(e) => { e.stopPropagation(); setCurrentIndex((prev) => (prev + 1) % images.length); }}
        className="absolute right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
      >
        <ChevronRight size={28} />
      </button>
      
      {/* Counter */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm">
        {currentIndex + 1} / {images.length}
      </div>
    </div>
  );
};

const ServicesSection = () => {
  const [specialties, setSpecialties] = useState(defaultSpecialties);
  const [content, setContent] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch specialties
        const specResponse = await fetch(`${API_URL}/api/specialties`);
        if (specResponse.ok) {
          const data = await specResponse.json();
          if (data.length > 0) {
            const mergedData = data.map(spec => {
              // Build images array from API or defaults
              let images = [];
              if (spec.images && spec.images.length > 0) {
                images = spec.images.map(img => img.startsWith('/api') ? `${API_URL}${img}` : img);
              } else if (spec.image_url) {
                images = [spec.image_url.startsWith('/api') ? `${API_URL}${spec.image_url}` : spec.image_url];
              } else {
                images = defaultImages[spec.id] || defaultImages['renovation'];
              }
              return { ...spec, images };
            });
            setSpecialties(mergedData);
          }
        }
        
        // Fetch content
        const contentResponse = await fetch(`${API_URL}/api/content`);
        if (contentResponse.ok) {
          setContent(await contentResponse.json());
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const keyFigures = content ? [
    { value: content.key_figure1_value, label: content.key_figure1_label },
  ].filter(f => f.value) : [
    { value: "200", label: "Collaborateurs actifs" }
  ];

  const renovationInfo = content ? {
    title: content.renovation_title,
    description: content.renovation_description
  } : defaultRenovation;

  const openLightbox = (images, index) => {
    setLightbox({ images, index });
  };

  return (
    <section id="services" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-stone-500 text-sm tracking-[0.25em] uppercase mb-3">
            Nos expertises
          </p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-stone-800 font-light">
            Notre Savoir-faire
          </h2>
          <div className="w-16 h-px bg-stone-300 mx-auto mt-6" />
          <p className="text-stone-600 max-w-2xl mx-auto mt-6 leading-relaxed">
            Des moyens humains, matériels et logistiques au service de votre projet.
          </p>
        </div>

        {/* Key Figures */}
        <div className="flex justify-center mb-20">
          {keyFigures.map((figure, index) => {
            const IconComponent = figureIcons[index];
            return (
              <div key={index} className="text-center p-8 bg-stone-50 rounded-lg max-w-xs w-full">
                <IconComponent className="w-10 h-10 text-stone-600 mx-auto mb-4" />
                <p className="font-serif text-4xl md:text-5xl text-stone-800 font-light mb-2">
                  {figure.value}
                </p>
                <p className="text-stone-500 text-sm tracking-wide uppercase">
                  {figure.label}
                </p>
              </div>
            );
          })}
        </div>

        {/* Renovation Specialty Section */}
        <div className="mb-16 p-8 md:p-12 bg-stone-800 rounded-lg text-white text-center">
          <h3 className="font-serif text-2xl md:text-3xl font-light mb-4">
            {renovationInfo.title}
          </h3>
          <p className="text-stone-300 leading-relaxed max-w-3xl mx-auto">
            {renovationInfo.description}
          </p>
        </div>

        {/* Specialties Grid with Carousel */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {specialties.map((specialty) => (
            <div
              key={specialty.id}
              className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-500"
            >
              {/* Image Carousel */}
              <div className="aspect-[4/3] overflow-hidden">
                <ImageCarousel 
                  images={specialty.images || defaultImages[specialty.id] || defaultImages['renovation']}
                  title={specialty.title}
                  onImageClick={openLightbox}
                />
              </div>
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none" />
              
              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white pointer-events-none">
                <h3 className="font-serif text-xl md:text-2xl mb-2">
                  {specialty.title}
                </h3>
                <p className="text-white/80 text-sm leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {specialty.description}
                </p>
                {specialty.images && specialty.images.length > 1 && (
                  <p className="text-white/60 text-xs mt-2">
                    {specialty.images.length} photos • Cliquez pour agrandir
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <Lightbox 
          images={lightbox.images} 
          initialIndex={lightbox.index} 
          onClose={() => setLightbox(null)} 
        />
      )}
    </section>
  );
};

export default ServicesSection;
